import React, { useState, useEffect, useCallback } from 'react';
import { LayoutGrid, BarChart3, Table2, Plus, Calendar, Target, Zap } from 'lucide-react';
import styles from './TaskflowView.module.css';

type ViewMode = 'board' | 'roadmap' | 'table';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  estimate?: number;
  agent?: string;
  confidence?: number;
  readiness?: number;
  cycleId?: string;
  blockedReason?: string;
  owner?: string;
}

interface Cycle {
  id: string;
  name: string;
  goal?: string;
  startDate: string;
  endDate: string;
  capacityPoints: number;
  allocatedPoints: number;
  completedPoints: number;
  status: string;
  taskCount?: number;
  tasks?: Task[];
}

const STATUS_COLUMNS = [
  { key: 'open', label: 'Inbox' },
  { key: 'ready', label: 'Ready' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'waiting_for_input', label: 'Waiting' },
  { key: 'completed', label: 'Done' },
];

function priorityClass(p: string) {
  if (p === 'urgent' || p === 'critical') return styles.priCritical;
  if (p === 'high') return styles.priHigh;
  if (p === 'medium') return styles.priMedium;
  return styles.priLow;
}

function statusBarClass(s: string) {
  if (s === 'completed') return styles.barDone;
  if (s === 'in_progress') return styles.barInProgress;
  if (s === 'blocked' || s === 'cancelled') return styles.barBlocked;
  return styles.barPlanned;
}

function statusBadgeClass(s: string) {
  if (s === 'completed') return styles.statusCompleted;
  if (s === 'in_progress') return styles.statusInProgress;
  if (s === 'open') return styles.statusOpen;
  if (s === 'waiting_for_input') return styles.statusWaiting;
  if (s === 'cancelled') return styles.statusCancelled;
  return styles.statusBlocked;
}

function confClass(c: number) {
  if (c >= 70) return styles.confHigh;
  if (c >= 40) return styles.confMed;
  return styles.confLow;
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}

export const TaskflowView: React.FC<{ tasks: Task[]; onTaskClick?: (id: string) => void }> = ({
  tasks,
  onTaskClick,
}) => {
  const [view, setView] = useState<ViewMode>('board');
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  // Fetch cycles
  const loadCycles = useCallback(async () => {
    try {
      const res = await fetch('/_api/taskingbot/cycles', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'roadmap' }),
      });
      if (res.ok) {
        const data = await res.json();
        setCycles(data.roadmap || []);
      }
    } catch {}
  }, []);

  useEffect(() => { loadCycles(); }, [loadCycles]);

  // Filter tasks by cycle
  const filteredTasks = selectedCycle === 'all'
    ? tasks
    : selectedCycle === 'backlog'
      ? tasks.filter(t => !t.cycleId)
      : tasks.filter(t => t.cycleId === selectedCycle);

  // Create new cycle
  const handleCreateCycle = async () => {
    const name = prompt('Cycle name (e.g., "Sprint 15 — Platform Launch"):');
    if (!name) return;
    setLoading(true);
    try {
      await fetch('/_api/taskingbot/cycles', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', name }),
      });
      await loadCycles();
    } catch {}
    setLoading(false);
  };

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${view === 'board' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('board')}
          >
            <LayoutGrid size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Board
          </button>
          <button
            className={`${styles.viewBtn} ${view === 'roadmap' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('roadmap')}
          >
            <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Roadmap
          </button>
          <button
            className={`${styles.viewBtn} ${view === 'table' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('table')}
          >
            <Table2 size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Table
          </button>
        </div>

        <select
          className={styles.cycleSelector}
          value={selectedCycle}
          onChange={(e) => setSelectedCycle(e.target.value)}
        >
          <option value="all">All Cycles</option>
          <option value="backlog">Backlog</option>
          {cycles.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <div className={styles.toolbarSpacer} />

        <button className={styles.addCycleBtn} onClick={handleCreateCycle} disabled={loading}>
          <Plus size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> New Cycle
        </button>
      </div>

      {/* Views */}
      {view === 'board' && <BoardView tasks={filteredTasks} onTaskClick={onTaskClick} />}
      {view === 'roadmap' && <RoadmapView cycles={cycles} tasks={tasks} onTaskClick={onTaskClick} />}
      {view === 'table' && <TableView tasks={filteredTasks} onTaskClick={onTaskClick} />}
    </div>
  );
};

// ── Board View (Kanban) ──────────────────────────────────────────────

const BoardView: React.FC<{ tasks: Task[]; onTaskClick?: (id: string) => void }> = ({ tasks, onTaskClick }) => {
  return (
    <div className={styles.boardContainer}>
      {STATUS_COLUMNS.map(col => {
        const colTasks = tasks.filter(t => {
          if (col.key === 'open') return t.status === 'open' || t.status === 'inbox' || t.status === 'ready';
          return t.status === col.key;
        });
        const points = colTasks.reduce((s, t) => s + (t.estimate || 1), 0);

        return (
          <div key={col.key} className={styles.boardColumn}>
            <div className={styles.columnHeader}>
              {col.label}
              <span className={styles.columnCount}>{colTasks.length}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.72rem' }}>{points}pt</span>
            </div>
            <div className={styles.columnCards}>
              {colTasks.map(task => (
                <div
                  key={task.id}
                  className={styles.taskCard}
                  onClick={() => onTaskClick?.(task.id)}
                >
                  <div className={styles.taskTitle}>{task.title}</div>
                  <div className={styles.taskMeta}>
                    <span className={`${styles.taskPriority} ${priorityClass(task.priority)}`} />
                    {task.estimate && (
                      <span className={styles.taskEstimate}>
                        <Zap size={10} /> {task.estimate}pt
                      </span>
                    )}
                    {task.agent && <span className={styles.taskAgent}>{task.agent}</span>}
                  </div>
                  {task.confidence != null && (
                    <div className={styles.confidenceBar}>
                      <div
                        className={`${styles.confidenceFill} ${confClass(task.confidence)}`}
                        style={{ width: `${task.confidence}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
              {colTasks.length === 0 && (
                <div style={{ color: 'var(--muted-foreground)', fontSize: '0.82rem', textAlign: 'center', padding: 16, opacity: 0.5 }}>
                  No tasks
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Roadmap View ─────────────────────────────────────────────────────

const RoadmapView: React.FC<{ cycles: Cycle[]; tasks?: Task[]; onTaskClick?: (id: string) => void }> = ({ cycles, tasks = [], onTaskClick }) => {
  // Build backlog tasks (tasks not assigned to any cycle)
  const cycleTaskIds = new Set(cycles.flatMap(c => (c.tasks || []).map(t => t.id)));
  const backlogTasks = tasks.filter(t => !cycleTaskIds.has(t.id) && t.status !== 'completed' && t.status !== 'cancelled');

  // Auto-group backlog by status for a useful roadmap even without cycles
  const statusGroups: Record<string, Task[]> = {};
  for (const t of backlogTasks) {
    const group = t.status === 'in_progress' || t.status === 'running' ? 'In Progress'
      : t.status === 'waiting_for_input' ? 'Blocked'
      : 'Backlog';
    (statusGroups[group] ??= []).push(t);
  }

  const renderTaskRow = (task: Task | { id: string; title: string; status: string; confidence?: number }) => {
    const barWidth = task.status === 'completed' ? 100
      : task.status === 'in_progress' || task.status === 'running' ? ((task as any).confidence || 50)
      : task.status === 'open' ? 5
      : 15;

    return (
      <div
        key={task.id}
        className={styles.roadmapTaskRow}
        onClick={() => onTaskClick?.(task.id)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.roadmapTaskName}>{task.title}</div>
        <div className={styles.roadmapTaskBar}>
          <div
            className={`${styles.roadmapTaskBarFill} ${statusBarClass(task.status)}`}
            style={{ width: `${barWidth}%` }}
          >
            {barWidth > 30 ? task.title : ''}
          </div>
        </div>
        <div className={`${styles.roadmapTaskStatus} ${statusBadgeClass(task.status)}`}>
          {task.status.replace(/_/g, ' ')}
        </div>
      </div>
    );
  };

  if (cycles.length === 0 && backlogTasks.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Calendar size={40} style={{ opacity: 0.3 }} />
        <div className={styles.emptyTitle}>No Tasks Yet</div>
        <div className={styles.emptyDesc}>Tasks will appear here as they're created. Create a cycle to organize into sprints.</div>
      </div>
    );
  }

  return (
    <div className={styles.roadmapContainer}>
      {/* Cycle-based sections */}
      {cycles.map(cycle => (
        <div key={cycle.id} className={styles.roadmapCycle}>
          <div className={styles.roadmapCycleHeader}>
            <div>
              <div className={styles.roadmapCycleName}>
                <Target size={16} style={{ marginRight: 6, verticalAlign: -3, color: 'var(--primary)' }} />
                {cycle.name}
              </div>
              <div className={styles.roadmapCycleDates}>
                {formatDate(cycle.startDate)} — {formatDate(cycle.endDate)}
                {cycle.goal && <span style={{ marginLeft: 12, fontStyle: 'italic' }}>"{cycle.goal}"</span>}
              </div>
            </div>
            <div className={styles.roadmapProgress}>
              <div className={styles.roadmapProgressBar}>
                <div
                  className={styles.roadmapProgressFill}
                  style={{ width: `${cycle.allocatedPoints > 0 ? Math.round((cycle.completedPoints / cycle.allocatedPoints) * 100) : 0}%` }}
                />
              </div>
              <span className={styles.roadmapProgressText}>
                {cycle.completedPoints}/{cycle.allocatedPoints}pt
              </span>
            </div>
          </div>

          <div className={styles.roadmapTasks}>
            {(cycle.tasks || []).map(renderTaskRow)}
            {(!cycle.tasks || cycle.tasks.length === 0) && (
              <div style={{ color: 'var(--muted-foreground)', fontSize: '0.85rem', padding: '8px 0' }}>
                No tasks in this cycle
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Unassigned tasks — always show so roadmap is never empty */}
      {Object.entries(statusGroups).map(([group, groupTasks]) => (
        <div key={group} className={styles.roadmapCycle}>
          <div className={styles.roadmapCycleHeader}>
            <div>
              <div className={styles.roadmapCycleName}>
                <Zap size={16} style={{ marginRight: 6, verticalAlign: -3, color: group === 'In Progress' ? '#3b82f6' : group === 'Blocked' ? '#ef4444' : 'var(--muted-foreground)' }} />
                {group}
              </div>
              <div className={styles.roadmapCycleDates}>
                {groupTasks.length} task{groupTasks.length !== 1 ? 's' : ''} not assigned to a cycle
              </div>
            </div>
            <div className={styles.roadmapProgress}>
              <span className={styles.roadmapProgressText}>
                {groupTasks.length} task{groupTasks.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className={styles.roadmapTasks}>
            {groupTasks.map(renderTaskRow)}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Table View ───────────────────────────────────────────────────────

const TableView: React.FC<{ tasks: Task[]; onTaskClick?: (id: string) => void }> = ({ tasks, onTaskClick }) => {
  if (tasks.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Table2 size={40} style={{ opacity: 0.3 }} />
        <div className={styles.emptyTitle}>No Tasks</div>
        <div className={styles.emptyDesc}>Tasks will appear here when created.</div>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Est</th>
            <th>Agent</th>
            <th>Confidence</th>
            <th>Readiness</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map(task => (
            <tr key={task.id} onClick={() => onTaskClick?.(task.id)} style={{ cursor: 'pointer' }}>
              <td>
                <span style={{ fontWeight: 600 }}>{task.title}</span>
                {task.blockedReason && (
                  <div style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 2 }}>
                    Blocked: {task.blockedReason}
                  </div>
                )}
              </td>
              <td>
                <span className={`${styles.statusBadge} ${statusBadgeClass(task.status)}`}>
                  {task.status.replace(/_/g, ' ')}
                </span>
              </td>
              <td>
                <span className={`${styles.taskPriority} ${priorityClass(task.priority)}`} style={{ display: 'inline-block', marginRight: 6 }} />
                {task.priority}
              </td>
              <td>
                {task.estimate && <span className={styles.taskEstimate}>{task.estimate}pt</span>}
              </td>
              <td>{task.agent || '—'}</td>
              <td>
                {task.confidence != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className={styles.confidenceBar} style={{ width: 60 }}>
                      <div
                        className={`${styles.confidenceFill} ${confClass(task.confidence)}`}
                        style={{ width: `${task.confidence}%` }}
                      />
                    </div>
                    <span style={{ fontSize: '0.78rem' }}>{task.confidence}%</span>
                  </div>
                )}
              </td>
              <td>
                {task.readiness != null && (
                  <span style={{ fontSize: '0.85rem' }}>{task.readiness}%</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TaskflowView;
